/**
 * Captura de audio vía MediaRecorder (webm / mp4 iOS).
 * Soporta pause/resume y expone el MediaStream para AnalyserNode (vúmetro).
 */

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/aac',
  ]
  for (let i = 0; i < candidates.length; i++) {
    const t = candidates[i]
    try {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) {
        return t
      }
    } catch (e) {
      /* ignore */
    }
  }
  return ''
}

export function isMediaRecorderSupported() {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  )
}

export function canPauseMediaRecorder() {
  try {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof MediaRecorder.prototype.pause === 'function' &&
      typeof MediaRecorder.prototype.resume === 'function'
    )
  } catch (e) {
    return false
  }
}

export class AudioRecorder {
  constructor() {
    /** @type {MediaStream|null} */
    this._stream = null
    /** @type {MediaRecorder|null} */
    this._recorder = null
    /** @type {Blob[]} */
    this._chunks = []
    /** @type {number} */
    this._startedAt = 0
    /** @type {number} */
    this._pausedAccumMs = 0
    /** @type {number} */
    this._pauseStartedAt = 0
    /** @type {string} */
    this._mimeType = ''
    /** @type {Promise<{ blob: Blob, mimeType: string, durationMs: number, startedAt: number }>|null} */
    this._stopPromise = null
  }

  /** @returns {MediaStream|null} */
  getStream() {
    return this._stream
  }

  isRecording() {
    return !!(this._recorder && this._recorder.state === 'recording')
  }

  isPaused() {
    return !!(this._recorder && this._recorder.state === 'paused')
  }

  isActive() {
    return !!(
      this._recorder &&
      (this._recorder.state === 'recording' || this._recorder.state === 'paused')
    )
  }

  /**
   * Tiempo efectivo de grabación (excluye pausas).
   * @returns {number}
   */
  getElapsedMs() {
    if (!this._startedAt) return 0
    const now = Date.now()
    let paused = this._pausedAccumMs
    if (this._pauseStartedAt) paused += now - this._pauseStartedAt
    return Math.max(0, now - this._startedAt - paused)
  }

  /**
   * @returns {Promise<void>}
   */
  async start() {
    if (this.isActive()) return
    if (!isMediaRecorderSupported()) {
      throw new Error('MediaRecorder no soportado en este dispositivo')
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
      },
      video: false,
    })

    this._stream = stream
    this._chunks = []
    this._pausedAccumMs = 0
    this._pauseStartedAt = 0
    this._mimeType = pickMimeType()
    const options = this._mimeType ? { mimeType: this._mimeType } : undefined
    let recorder
    try {
      recorder = options ? new MediaRecorder(stream, options) : new MediaRecorder(stream)
    } catch (e) {
      recorder = new MediaRecorder(stream)
    }
    this._mimeType = recorder.mimeType || this._mimeType || 'audio/webm'
    this._recorder = recorder

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) this._chunks.push(ev.data)
    }

    this._startedAt = Date.now()
    try {
      recorder.start(250)
    } catch (e) {
      recorder.start()
    }
  }

  pause() {
    const recorder = this._recorder
    if (!recorder || recorder.state !== 'recording') return false
    if (typeof recorder.pause !== 'function') return false
    try {
      recorder.pause()
      this._pauseStartedAt = Date.now()
      return true
    } catch (e) {
      return false
    }
  }

  resume() {
    const recorder = this._recorder
    if (!recorder || recorder.state !== 'paused') return false
    if (typeof recorder.resume !== 'function') return false
    try {
      if (this._pauseStartedAt) {
        this._pausedAccumMs += Date.now() - this._pauseStartedAt
        this._pauseStartedAt = 0
      }
      recorder.resume()
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * @returns {Promise<{ blob: Blob, mimeType: string, durationMs: number, startedAt: number }>}
   */
  stop() {
    if (this._stopPromise) return this._stopPromise
    const recorder = this._recorder
    if (!recorder || recorder.state === 'inactive') {
      this._cleanupStream()
      return Promise.reject(new Error('No hay grabación activa'))
    }

    this._stopPromise = new Promise((resolve, reject) => {
      const startedAt = this._startedAt
      const mimeType = this._mimeType || 'audio/webm'
      const durationMs = this.getElapsedMs()

      const finish = () => {
        this._stopPromise = null
        this._recorder = null
        this._cleanupStream()
        this._pauseStartedAt = 0
        if (!this._chunks.length) {
          reject(new Error('Grabación vacía'))
          return
        }
        const blob = new Blob(this._chunks, { type: mimeType })
        this._chunks = []
        resolve({ blob, mimeType, durationMs, startedAt })
      }

      recorder.onstop = finish
      recorder.onerror = (ev) => {
        this._stopPromise = null
        this._recorder = null
        this._cleanupStream()
        reject((ev && ev.error) || new Error('Error de MediaRecorder'))
      }

      try {
        if (recorder.state === 'recording' || recorder.state === 'paused') {
          recorder.requestData()
        }
      } catch (e) {
        /* ignore */
      }
      try {
        recorder.stop()
      } catch (e) {
        this._stopPromise = null
        this._cleanupStream()
        reject(e)
      }
    })

    return this._stopPromise
  }

  /** Cancela sin devolver blob. */
  cancel() {
    try {
      if (this._recorder && this._recorder.state !== 'inactive') {
        this._recorder.onstop = null
        this._recorder.onerror = null
        this._recorder.stop()
      }
    } catch (e) {
      /* ignore */
    }
    this._recorder = null
    this._chunks = []
    this._stopPromise = null
    this._pausedAccumMs = 0
    this._pauseStartedAt = 0
    this._startedAt = 0
    this._cleanupStream()
  }

  _cleanupStream() {
    if (this._stream) {
      try {
        this._stream.getTracks().forEach((t) => t.stop())
      } catch (e) {
        /* ignore */
      }
    }
    this._stream = null
  }
}

export default AudioRecorder
